/**
 * Lodging-flavored homepage sections.
 *
 * Seven section types seeded by db-setup.ts when siteType === 'lodging':
 *   - availability_search   — photo hero with the availability search card
 *   - featured_properties   — N featured property cards
 *   - host_intro            — meet the host / team callout
 *   - area_highlights       — local guide teaser
 *   - reviews_teaser        — N recent guest reviews (placeholder until
 *                             Phase 3 ships the reviews feature)
 *   - direct_book_pitch     — "why book direct" appeal
 *   - newsletter            — newsletter signup (reused from existing component)
 *
 * Each renderer receives the homepage_sections row as `section`. Content
 * shape is documented per renderer; admin editing of these sections is via
 * the existing homepage builder JSON edit modal (form editors are a follow-up).
 *
 * Visual language ("quiet-luxury editorial"): serif display headings with a
 * terra eyebrow via SectionHeading, elevated PropertyCards, max-w-6xl
 * containers, py-16 section rhythm (Look density remaps it per tenant).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, BadgePercent, MessageCircle, ShieldCheck, Star } from 'lucide-react';
import api from '@/lib/api';
import { useSettings } from '@/hooks/useProducts';
import AvailabilitySearchBar from '@/components/property/AvailabilitySearchBar';
import LodgingHero from '@/components/lodging/LodgingHero';
import PropertyCard, { type PropertyCardData } from '@/components/lodging/PropertyCard';
import PropertyCardSkeleton from '@/components/lodging/PropertyCardSkeleton';
import RatingStars from '@/components/lodging/RatingStars';
import SectionHeading from '@/components/lodging/SectionHeading';
import Skeleton from '@/components/ui/Skeleton';
// Generic scroll-reveal (fade + slide-up, reduced-motion aware) despite the
// saas/ home — see Phase 5 of the lodging redesign.
import Reveal from '@/components/saas/Reveal';

interface SectionProps {
  section: {
    id?: string;
    title?: string | null;
    subtitle?: string | null;
    content?: Record<string, any> | null;
  };
}

// ---------------------------------------------------------------------------
// availability_search — hero with date picker + guest count + Search CTA
// content: { cta_label, cta_href, show_guest_count, hero_image, eyebrow }
// ---------------------------------------------------------------------------
export function AvailabilitySearchHero({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const c = section.content || {};

  return (
    <LodgingHero
      headingId={section.id ? `lodging-hero-${section.id}` : undefined}
      eyebrow={c.eyebrow}
      title={section.title || t('sections.hero.defaultTitle')}
      subtitle={section.subtitle}
      media={
        c.hero_image ? (
          <img src={c.hero_image} alt="" className="h-full w-full object-cover" />
        ) : undefined
      }
      searchBar={
        <div className="space-y-4">
          <AvailabilitySearchBar
            targetHref={c.cta_href || '/properties'}
            ctaLabel={c.cta_label || undefined}
            showGuestCount={c.show_guest_count !== false}
          />
          {/* Prominent "Book Now" CTA alongside the search (hide with
              content.show_book_now === false). On multi-property sites it opens
              the stays list to pick a property. */}
          {c.show_book_now !== false && (
            <div className="text-center">
              <Link
                to={c.book_href || c.cta_href || '/properties'}
                className="inline-flex items-center rounded-full bg-cta px-6 py-2.5 font-semibold text-cta-fg transition-colors hover:bg-cta-deep"
              >
                {c.book_now_label || t('common:bookNow', { defaultValue: 'Book Now' })}
              </Link>
            </div>
          )}
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// featured_properties — show N published properties as cards
// content: { count, show_price_from, eyebrow, cta_href }
// ---------------------------------------------------------------------------
export function FeaturedProperties({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const c = section.content || {};
  const count = Math.max(1, Math.min(12, Number(c.count || 3)));
  const [properties, setProperties] = useState<PropertyCardData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ properties: PropertyCardData[] }>(`/properties?limit=${count}`)
      .then((r) => setProperties(r.properties || []))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, [count]);

  // A lodging site with zero published properties shouldn't advertise an
  // empty shelf — skip the section entirely once the fetch settles.
  if (!loading && properties.length === 0) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <SectionHeading
          eyebrow={c.eyebrow || t('sections.featured.eyebrow')}
          title={section.title}
          subtitle={section.subtitle}
        />
        <Link
          to={c.cta_href || '/properties'}
          className="group/link inline-flex items-center gap-1.5 text-sm font-medium text-sage-800 transition-colors hover:text-sage-900"
        >
          {t('sections.featured.viewAll')}
          <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-0.5" aria-hidden="true" />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading
          ? Array.from({ length: Math.min(count, 3) }).map((_, i) => <PropertyCardSkeleton key={i} />)
          : properties.map((p, i) => (
              <Reveal key={p.id} delayMs={i * 80} className="h-full">
                <PropertyCard property={p} showPrice={c.show_price_from !== false} className="h-full" />
              </Reveal>
            ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// host_intro — text block with CTA
// content: { body, cta_label, cta_href, image_url, eyebrow }
// ---------------------------------------------------------------------------
export function HostIntro({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const { data: settingsData } = useSettings();
  const c = section.content || {};
  // Prefer a section-specific photo, then the tenant's host portrait (admin →
  // Settings → Host), so setting the host photo once fills this section too.
  const photoUrl: string | undefined = c.image_url || settingsData?.settings?.host_photo_url;
  return (
    <section className="bg-surface-soft py-16">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 sm:px-6 md:grid-cols-2 lg:gap-14">
        <Reveal>
          <div className="relative">
            {/* Offset accent frame — a quiet brass/terra flourish behind the photo. */}
            <div
              className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl border border-terra/40"
              aria-hidden="true"
            />
            <div className="relative aspect-square overflow-hidden rounded-2xl bg-surface-muted shadow-md">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-sage-100 to-surface-muted" />
              )}
            </div>
          </div>
        </Reveal>
        <Reveal delayMs={120}>
          <SectionHeading
            eyebrow={c.eyebrow || t('sections.host.eyebrow')}
            title={section.title}
            subtitle={section.subtitle}
          />
          {c.body && <p className="mt-4 leading-relaxed text-ink-soft">{c.body}</p>}
          {c.cta_label && c.cta_href && (
            <Link
              to={c.cta_href}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-cta px-6 py-3 font-medium text-cta-fg transition-colors hover:bg-cta-deep"
            >
              {c.cta_label}
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// area_highlights — local guide teaser with CTA
// content: { cta_label, cta_href, show_n_picks, image_url, eyebrow }
// ---------------------------------------------------------------------------
export function AreaHighlights({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const c = section.content || {};
  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <SectionHeading
        align="center"
        eyebrow={c.eyebrow || t('sections.area.eyebrow')}
        title={section.title}
        subtitle={section.subtitle}
        className="mb-8"
      />
      {c.image_url && (
        <Reveal>
          <div className="mb-8 aspect-[3/1] overflow-hidden rounded-2xl bg-surface-muted shadow-md">
            <img src={c.image_url} alt="" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      )}
      {c.cta_label && c.cta_href && (
        <div className="text-center">
          <Link
            to={c.cta_href}
            className="inline-flex items-center gap-2 font-medium text-sage-800 underline-offset-4 transition-colors hover:text-sage-900 hover:underline"
          >
            {c.cta_label}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// reviews_teaser — serif pull-quote cards from /lodging-reviews/top
// content: { count, min_rating }
// ---------------------------------------------------------------------------
interface TeaserReview {
  id: string;
  guest_name: string;
  rating: number;
  body: string;
  property_title?: string | null;
  property_slug?: string | null;
}

export function ReviewsTeaser({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const c = section.content || {};
  const count = Math.max(1, Math.min(12, Number(c.count || 3)));
  const minRating = Math.max(1, Math.min(5, Number(c.min_rating || 4)));
  const [reviews, setReviews] = useState<TeaserReview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ reviews: TeaserReview[] }>(`/lodging-reviews/top?limit=${count}&min_rating=${minRating}`)
      .then((r) => setReviews(r.reviews || []))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  }, [count, minRating]);

  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <SectionHeading
        align="center"
        eyebrow={t('sections.reviews.eyebrow')}
        title={section.title}
        subtitle={section.subtitle}
        className="mb-8"
      />
      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
            <div
              key={i}
              data-testid="review-teaser-skeleton"
              aria-hidden="true"
              className="rounded-2xl border border-line bg-surface p-6 shadow-sm space-y-3"
            >
              <Skeleton height="1rem" width="6rem" />
              <Skeleton height="0.875rem" width="100%" />
              <Skeleton height="0.875rem" width="83%" />
              <Skeleton height="0.875rem" width="50%" />
            </div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        // Quiet empty state — hosts without published reviews yet keep the
        // heading but never show a broken grid.
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-line bg-surface-soft px-4 py-2 text-sm text-ink-soft">
            <Star className="h-4 w-4 fill-terra text-terra" aria-hidden="true" />
            {t('sections.reviews.comingSoon')}
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r, i) => (
            <Reveal key={r.id} delayMs={i * 80} className="h-full">
              <figure className="flex h-full flex-col rounded-2xl border border-line bg-surface p-6 shadow-sm">
              <RatingStars rating={r.rating} className="mb-3" />
              <blockquote className="flex-1 font-display text-lg leading-relaxed text-ink line-clamp-5">
                “{r.body}”
              </blockquote>
              <figcaption className="mt-4 border-t border-line pt-3 text-sm">
                <span className="font-semibold text-ink">{r.guest_name}</span>
                {r.property_slug && r.property_title && (
                  <Link
                    to={`/properties/${r.property_slug}`}
                    className="block text-ink-muted transition-colors hover:text-sage-700"
                  >
                    {r.property_title}
                  </Link>
                )}
              </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// direct_book_pitch — "why book direct" appeal
// content: { body, cta_label, cta_href, eyebrow }
// ---------------------------------------------------------------------------
export function DirectBookPitch({ section }: SectionProps) {
  const { t } = useTranslation('properties');
  const c = section.content || {};
  return (
    // bg-cta is the fixed brand-dark band — it deliberately doesn't flip in
    // dark mode, so the light-on-dark styling here is mode-agnostic.
    <section className="bg-cta py-16 text-cta-fg">
      <Reveal className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <SectionHeading
          align="center"
          tone="inverse"
          eyebrow={c.eyebrow || t('sections.directBook.eyebrow')}
          title={section.title}
          subtitle={section.subtitle}
        />
        {c.body && <p className="mt-4 leading-relaxed text-white/85">{c.body}</p>}
        <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-white/90">
          <li className="flex items-center gap-2">
            <BadgePercent className="h-4 w-4 text-terra-light" aria-hidden="true" />
            {t('sections.directBook.perks.rate')}
          </li>
          <li className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-terra-light" aria-hidden="true" />
            {t('sections.directBook.perks.fees')}
          </li>
          <li className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-terra-light" aria-hidden="true" />
            {t('sections.directBook.perks.host')}
          </li>
        </ul>
        {c.cta_label && c.cta_href && (
          <Link
            to={c.cta_href}
            className="mt-8 inline-block rounded-lg bg-cta-fg px-6 py-3 font-medium text-cta transition-opacity hover:opacity-90"
          >
            {c.cta_label}
          </Link>
        )}
      </Reveal>
    </section>
  );
}
